import Box from '@redbubble/design-system/src/Box';
import List, { ListItem } from '@redbubble/design-system/src/List';
import Modal, { ModalBody } from '@redbubble/design-system/src/Modal';
import Review from '@redbubble/design-system/src/Review';
import Text from '@redbubble/design-system/src/Text';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import he from 'he';
import { Review as ReviewData } from 'types/shop-bff/types';
import { ProductReviewsSkeleton } from './ProductReviewsSkeleton';

const NUM_LOADING_ITEMS = 3;

export const messages = defineMessages({
  reviewTitle: {
    defaultMessage: 'Reviews',
  },
});

export type ReviewItem = Pick<
  ReviewData,
  'id' | 'authorName' | 'content' | 'createdAt' | 'score' | 'title'
>;

export interface ProductReviewModalProps {
  reviews: ReviewItem[] | null;
  loading?: boolean;
  isOpen?: boolean;
  onCloseRequested?: () => void;
}

export const ProductReviewModal = ({
  reviews,
  loading,
  isOpen,
  onCloseRequested,
}: ProductReviewModalProps) => {
  const intl = useIntl();

  if (!loading && !reviews) return null;

  const reviewTitle = intl.formatMessage(messages.reviewTitle);

  return (
    <Modal
      open={isOpen}
      onCloseRequested={onCloseRequested}
      accessibleTitle={reviewTitle}
    >
      <ModalBody padded={false}>
        <Box padding="l">
          <Text type="display3" element="h2" marginBottom="xxs" display="block">
            {reviewTitle}
          </Text>
          <List relaxed>
            {loading ? (
              <Box data-testid="product-reviews-modal-skeleton">
                {Array(NUM_LOADING_ITEMS)
                  .fill(true)
                  .map((_, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: noArrayIndexKey
                    <ListItem key={`loading-line-item-${i}`}>
                      <ProductReviewsSkeleton />
                    </ListItem>
                  ))}
              </Box>
            ) : (
              reviews?.map(
                ({ id, score, authorName, title, createdAt, content }) => (
                  <ListItem key={id}>
                    <Review
                      rating={score}
                      username={authorName}
                      title={he.decode(title)}
                      date={createdAt}
                    >
                      {he.decode(content)}
                    </Review>
                  </ListItem>
                )
              )
            )}
          </List>
        </Box>
      </ModalBody>
    </Modal>
  );
};
